import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, CardActions, Grid, Typography, Button, Chip, IconButton,
  LinearProgress, Skeleton, Alert, Tooltip, Paper, TextField, InputAdornment,
} from '@mui/material';
import {
  SmartToy, Battery90, BatteryCharging20, Wifi, WifiOff, Gamepad, Refresh,
  Memory, Speed, LocationOn, Search,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppHooks';
import { fetchMyRobots } from '../../features/robots/robotsSlice';
import { useWebSocket } from '../../hooks/useWebSocket';
import { requestRobotsOnline } from '../../utils/socketClient';
import { normalizeBatteryPercent } from '../../utils/battery';

const RobotList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { robots, isLoading, error } = useAppSelector((state) => state.robots);
  const { robotsOnline, subscribeToRobot, getRobotTelemetry, isConnected } = useWebSocket();
  const [lastRefresh, setLastRefresh] = useState(new Date());
  // v6.3.36: filter primarily by serial number (unique); friendly name and
  // model are also matched so the box is useful either way.
  const [search, setSearch] = useState('');
  const filteredRobots = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return robots;
    return robots.filter((r: any) =>
      `${r.serial_number || ''} ${r.friendly_name || ''} ${r.model_number || ''}`
        .toLowerCase().includes(q));
  }, [robots, search]);

  // Fetch robots on mount and periodically
  useEffect(() => {
    dispatch(fetchMyRobots());
  }, [dispatch]);
  
  // Request fresh online status after mount and periodically
  useEffect(() => {
    if (isConnected) {
      requestRobotsOnline();
      const interval = setInterval(requestRobotsOnline, 10000);
      return () => clearInterval(interval);
    }
  }, [isConnected]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchMyRobots());
      setLastRefresh(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Debounced re-fetch when robots come online/offline (prevents flickering)
  const robotsOnlineCount = robotsOnline.length;
  const prevOnlineCountRef = React.useRef(robotsOnlineCount);
  useEffect(() => {
    if (prevOnlineCountRef.current !== robotsOnlineCount) {
      prevOnlineCountRef.current = robotsOnlineCount;
      const timer = setTimeout(() => {
        dispatch(fetchMyRobots());
        setLastRefresh(new Date());
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [robotsOnlineCount, dispatch]);

  // Subscribe to all robots for telemetry
  useEffect(() => {
    robots.forEach((robot) => {
      subscribeToRobot(robot.serial_number);
    });
  }, [robots, subscribeToRobot]);

  const handleRefresh = useCallback(() => {
    dispatch(fetchMyRobots());
    setLastRefresh(new Date());
  }, [dispatch]);

  if (isLoading && robots.length === 0) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>My Robots</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}>
              <Skeleton variant="rounded" height={280} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>My Robots</Typography>
          <Typography variant="body2" color="text.secondary">
            {robots.length} robot{robots.length !== 1 ? 's' : ''} • {robotsOnline.length} online
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={isConnected ? <Wifi /> : <WifiOff />}
            label={isConnected ? 'WebSocket Connected' : 'Disconnected'}
            color={isConnected ? 'success' : 'error'}
            size="small"
          />
          <Button startIcon={<Refresh />} onClick={handleRefresh} disabled={isLoading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {robots.length > 0 && (
        <TextField
          size="small" fullWidth placeholder="Filter by serial number, name or model…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 3, maxWidth: 420 }}
          InputProps={{ startAdornment: (
            <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
          ) }}
        />
      )}

      {robots.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <SmartToy sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>No Robots Yet</Typography>
          <Typography color="text.secondary">
            Contact an administrator to assign robots to your account.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredRobots.length === 0 && (
            <Grid item xs={12}>
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No robots match "{search}".
                </Typography>
              </Card>
            </Grid>
          )}
          {filteredRobots.map((robot) => {
            const isOnline = robotsOnline.includes(robot.serial_number);
            const telemetry = getRobotTelemetry(robot.serial_number);
            const battery = telemetry?.battery;
            const pose = (() => {
              const p = telemetry?.pose as any;
              if (!p) return null;
              if (typeof p.x === 'number') return p;
              const inner = p.pose || p;
              const pos = inner.position || inner;
              const ori = inner.orientation;
              if (typeof pos.x !== 'number') return null;
              let theta = 0;
              if (ori && typeof ori.z === 'number' && typeof ori.w === 'number') {
                theta = 2 * Math.atan2(ori.z, ori.w);
              } else if (typeof p.theta === 'number') {
                theta = p.theta;
              }
              return { x: pos.x, y: pos.y, theta };
            })();
            const diagnostics = telemetry?.diagnostics;
            const batteryPercent = normalizeBatteryPercent(battery?.percentage) ?? 0;
            const isCharging = battery?.is_charging || false;

            return (
              <Grid item xs={12} md={6} lg={4} key={robot.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: 2,
                          bgcolor: isOnline ? 'rgba(105, 240, 174, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <SmartToy sx={{ color: isOnline ? 'success.main' : 'text.secondary', fontSize: 28 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* v6.3.36: serial is the unique identifier and leads */}
                        <Typography variant="h6" fontWeight={700} sx={{ wordBreak: 'break-all', lineHeight: 1.3 }}>
                          {robot.serial_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {robot.model_number}{robot.friendly_name ? ` • ${robot.friendly_name}` : ''}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={isOnline ? 'Online' : 'Offline'}
                        sx={{
                          bgcolor: isOnline ? 'rgba(105, 240, 174, 0.2)' : 'rgba(255, 82, 82, 0.2)',
                          color: isOnline ? '#69f0ae' : '#ff5252',
                        }}
                      />
                    </Box>

                    {/* Telemetry Data */}
                    {isOnline && telemetry ? (
                      <Box>
                        {/* Battery */}
                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isCharging ? (
                                <BatteryCharging20 sx={{ color: 'success.main', fontSize: 20 }} />
                              ) : (
                                <Battery90 sx={{ fontSize: 20 }} />
                              )}
                              <Typography variant="body2">Battery</Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={600}>
                              {batteryPercent.toFixed(0)}%
                              {isCharging && <span style={{ color: '#69f0ae', marginLeft: 4 }}>⚡</span>}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(batteryPercent, 100)}
                            sx={{
                              height: 6, borderRadius: 3,
                              bgcolor: 'rgba(255,255,255,0.1)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: batteryPercent > 50 ? 'success.main' : batteryPercent > 20 ? 'warning.main' : 'error.main',
                              },
                            }}
                          />
                          {battery?.voltage && (
                            <Typography variant="caption" color="text.secondary">
                              {battery.voltage.toFixed(1)}V
                            </Typography>
                          )}
                        </Box>

                        {/* Position */}
                        {(pose?.x !== undefined || pose?.y !== undefined) && (
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <LocationOn sx={{ fontSize: 20 }} />
                              <Typography variant="body2">Position</Typography>
                            </Box>
                            <Grid container spacing={1}>
                              <Grid item xs={4}>
                                <Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                                  <Typography variant="caption" color="text.secondary">X</Typography>
                                  <Typography variant="body2" fontWeight={500}>{pose?.x?.toFixed(2) || '0.00'}</Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={4}>
                                <Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                                  <Typography variant="caption" color="text.secondary">Y</Typography>
                                  <Typography variant="body2" fontWeight={500}>{pose?.y?.toFixed(2) || '0.00'}</Typography>
                                </Paper>
                              </Grid>
                              <Grid item xs={4}>
                                <Paper sx={{ p: 0.5, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.03)' }}>
                                  <Typography variant="caption" color="text.secondary">θ</Typography>
                                  <Typography variant="body2" fontWeight={500}>
                                    {pose?.theta ? `${(pose.theta * 180 / Math.PI).toFixed(0)}°` : '0°'}
                                  </Typography>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>
                        )}

                        {/* Diagnostics */}
                        {(diagnostics?.cpu_percent !== undefined || diagnostics?.memory_mb !== undefined) && (
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            {diagnostics?.cpu_percent !== undefined && (
                              <Tooltip title="CPU Usage">
                                <Chip
                                  icon={<Memory sx={{ fontSize: 16 }} />}
                                  label={`${diagnostics.cpu_percent.toFixed(0)}%`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
                                />
                              </Tooltip>
                            )}
                            {diagnostics?.memory_mb !== undefined && (
                              <Tooltip title="Memory Usage">
                                <Chip
                                  icon={<Speed sx={{ fontSize: 16 }} />}
                                  label={`${diagnostics.memory_mb.toFixed(0)} MB`}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : isOnline ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Waiting for telemetry...
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Robot is offline
                        </Typography>
                        {robot.last_seen && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last seen: {new Date(robot.last_seen).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      startIcon={<Gamepad />}
                      onClick={() => navigate(`/robots/${robot.id}/control`)}
                      disabled={!isOnline}
                      fullWidth
                    >
                      Control
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Last refresh indicator */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'right' }}>
        Last refreshed: {lastRefresh.toLocaleTimeString()}
      </Typography>
    </Box>
  );
};

export default RobotList;
